import React, { useState } from 'react'
import Select from 'react-select'
import { useGetAllBusinesses, useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useSwitchBusiness } from '../useSwitchBusiness'

interface Props {
  children: React.ReactNode
  /** Smaller line above the subject — the parent record on a detail page. */
  eyebrow?: React.ReactNode
}

type Option = { value: number; label: string }

const switcherStyles = {
  container: (base: any) => ({ ...base, width: 260 }),
  control: (base: any) => ({
    ...base,
    minHeight: 32,
    borderColor: '#A044FF',
    boxShadow: 'none',
    ':hover': { borderColor: '#A044FF' },
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
  input: (base: any) => ({ ...base, fontSize: 14 }),
  placeholder: (base: any) => ({ ...base, fontSize: 14, color: '#989898' }),
  menu: (base: any) => ({ ...base, border: '1px solid #A044FF', zIndex: 60 }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected || state.isFocused ? '#F3E7FF' : 'transparent',
    color: '#1E1B4B',
    fontWeight: state.isSelected ? 600 : 400,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
}

export const PageBarSubject = ({ children, eyebrow }: Props) => {
  const business = useGetBusinessIfExists()
  const businesses = useGetAllBusinesses()
  const switchBusiness = useSwitchBusiness()
  const [switching, setSwitching] = useState(false)

  const options: Option[] = businesses.map(b => ({ value: b.id, label: b.businessName }))

  return (
    <div className="flex min-w-0 max-w-full flex-col sm:flex-row sm:items-center sm:gap-2">
      {business && (
        <>
          {/* The rail's switcher is hidden until the rail opens, so the name people
              already look at to confirm the account is also where they change it —
              matching the new dashboard's page title. The list floats under the name
              rather than replacing it, so opening it doesn't reflow the bar. */}
          <div className="relative min-w-0 max-w-full sm:max-w-[240px]">
            <button
              type="button"
              onClick={() => setSwitching(true)}
              title={`${business.businessName} — switch business`}
              className="flex min-w-0 max-w-full items-center gap-1 rounded-md text-left text-base font-semibold text-mm-primary-600 hover:text-mm-primary-800"
            >
              <span className="truncate">{business.businessName}</span>
              <i className="fas fa-chevron-down text-xs text-mm-primary-600" aria-hidden />
            </button>
            {switching && (
              <div className="absolute left-0 top-full z-50 mt-1">
                <Select
                  autoFocus
                  menuIsOpen
                  // The name is already shown just above; an empty field says "type
                  // to search" where a prefilled one reads as text to delete first.
                  controlShouldRenderValue={false}
                  options={options}
                  value={options.find(o => o.value === business.id) ?? null}
                  onChange={(option: any) => {
                    setSwitching(false)
                    switchBusiness(option.value)
                  }}
                  onMenuClose={() => setSwitching(false)}
                  styles={switcherStyles}
                  placeholder="Search businesses"
                  aria-label="Business"
                />
              </div>
            )}
          </div>
          <span className="hidden text-mm-neutral-1-400 sm:block" aria-hidden>
            ·
          </span>
        </>
      )}
      <div className="min-w-0">
        {eyebrow && <div className="truncate text-xxs text-mm-neutral-1-500">{eyebrow}</div>}
        {/* The subject has to out-weigh the actions and banners sharing the bar;
            at toolbar size it reads as another control rather than the page. */}
        <h1 className="truncate text-xl font-semibold text-mm-neutral-1-900">{children}</h1>
      </div>
    </div>
  )
}
